import type {
    GetQuoteById_quote_productLineItems_edges_node as ProductLineItem,
    GetPossibleProducts_quotePossibleProducts_edges as QuotePossibleProductsEdge,
    GetQuoteById_quote_total as Money,
} from 'nutshell-graphql-types';

import {formatCurrencyValue} from '../../../../../../util/format-currency-value';

import {TEMP_NEW_PRODUCT_ID} from '../helpers';
import type {FieldError} from '../helpers';

import {DEFAULT_PRODUCT_NAME, FIELD_NAMES, TEMPLATE_COLUMN_WIDTHS} from './consts';
import type {ColumnWidths} from './consts';

type DropdownOption = {
    value: string;
    label: string;
    sublabel?: string;
    subtitle?: string;
};

export const getNameColumnWidth = (
    showSku: boolean,
    showTerm: boolean,
    showDiscount: boolean,
    isEditable: boolean,
    isTemplate: boolean,
    columnWidths: ColumnWidths
) => {
    const skuWidth = showSku ? 0 : columnWidths.sku;
    const termWidth = showTerm ? 0 : columnWidths.term;
    const discountWidth = showDiscount ? 0 : columnWidths.discount;

    if (isTemplate) {
        return TEMPLATE_COLUMN_WIDTHS.name;
    }

    return isEditable
        ? columnWidths.name
        : columnWidths.name + skuWidth + termWidth + discountWidth;
};

export const getEditableColumnWidths = (columnWidths: ColumnWidths) => {
    return {
        ...columnWidths,
        name: columnWidths.name - 28, // 28px is the width of the handle bar
    };
};

// This allows the fixed amount label to match the market currency symbol
export const createDiscountOptions = (symbol: string): Array<DropdownOption> => {
    return [
        {value: 'PERCENT', label: `%`},
        {value: 'FIXED_AMOUNT', label: symbol},
    ];
};

/*
 * This creates the options for the product dropdown
 * by adding 'Other' to the end of the list
 * Includes SKU as subtitle and category as sublabel for better searchability
 */
export const createProductDropdownOptions = (
    quotePossibleProductsEdges: Array<QuotePossibleProductsEdge>
): Array<DropdownOption> => {
    const options = quotePossibleProductsEdges.map((product) => {
        return {
            value: product.node.id,
            label: product.node.name,
            subtitle: product.node.sku,
            sublabel:
                product.node.category && product.node.category.name
                    ? product.node.category.name
                    : 'No category',
        };
    });

    // @ts-expect-error
    options.push({
        value: `Other`,
        label: 'Other',
    });

    return options;
};

// Returns false if the value is numeric
export const isNumeric = (value: string): boolean => {
    // Check if input contains non-numeric characters
    // @ts-expect-error
    if (isNaN(value)) {
        return false;
    }

    // Check for multiple decimal points
    if (value.split('.').length > 2) {
        return false;
    }

    return true;
};

// Returns true if the value has more than num decimal places
export const hasMaxDecimalPlaces = (value: string, num: number) => {
    const parts = value.split('.');

    // if num is 0, then there should be no decimal point
    if (num === 0 && value.includes('.')) {
        return true;
    }

    // Ensure there are only num decimal places
    if (parts[1]) return parts[1].length > num;

    return false;
};

// Returns true if the value is longer than the max length
export const hasMaxLength = (value: string, length: number) => {
    return value.length > length;
};

// Returns true if the value is a positive number
export const isPositiveNumber = (value: string) => {
    if (value === '') return true; // This allows the user to clear the field

    return parseFloat(value) >= 0;
};

/*
 * This function validates:
 * 1. The value is numeric
 * 2. The value has at most maxDecimalPlaces decimal places
 * 3. The value has at most maxLength characters
 * Then it sets the error message if any errors are found
 * Returns true if there is an error, false otherwise
 */
export const hasNumberValidationError = (
    value: string,
    field: string,
    displayField: string, // The field to display in the error message
    maxDecimalPlaces: number,
    maxLength: number,
    setErrorMessage: (arg1: FieldError) => void
) => {
    if (!isNumeric(value)) {
        setErrorMessage({message: `${displayField} must be numeric`, field: field});

        return true;
    }

    if (!isPositiveNumber(value)) {
        setErrorMessage({message: `${displayField} must be positive`, field: field});

        return true;
    }

    if (hasMaxDecimalPlaces(value, maxDecimalPlaces)) {
        setErrorMessage({
            message: `${displayField} can only have ${maxDecimalPlaces} decimal places`,
            field: field,
        });

        return true;
    }

    if (hasMaxLength(value, maxLength)) {
        setErrorMessage({
            message: `${displayField} can only have ${maxLength} characters`,
            field: field,
        });

        return true;
    }

    return false;
};

export const resetErrorMessage = (setErrorMessage: (arg1: FieldError) => void) => {
    setErrorMessage({message: '', field: ''});
};

/*
 * This handles when a product field is changed
 * It validates the value and sets the error message if there is an error
 * Then it saves the product to the database
 */
export const onProductFieldChange = (
    field: string,
    value: string,
    subfield: string | null | undefined,
    item: ProductLineItem,
    handleSave: (index: number, updatedProduct: ProductLineItem) => void,
    index: number,
    setErrorMessage: (arg1: FieldError) => void
) => {
    if (field === FIELD_NAMES.name) {
        if (hasMaxLength(value, 75)) {
            setErrorMessage({message: 'Name can only have 75 characters', field: field});

            return;
        }
    }

    if (field === FIELD_NAMES.sku) {
        if (hasMaxLength(value, 20)) {
            setErrorMessage({message: 'SKU can only have 20 characters', field: field});

            return;
        }
    }

    if (field === FIELD_NAMES.quantity) {
        if (hasNumberValidationError(value, field, 'Quantity', 2, 10, setErrorMessage)) {
            return;
        }
    }

    if (field === FIELD_NAMES.price) {
        if (hasNumberValidationError(value, field, 'Price', 2, 11, setErrorMessage)) {
            return;
        }
    }

    if (field === FIELD_NAMES.discountNumber) {
        if (hasNumberValidationError(value, field, 'Discount', 2, 5, setErrorMessage)) {
            return;
        }
    }

    resetErrorMessage(setErrorMessage);
    saveUpdateProduct(item, field, subfield, value, handleSave, index);
};

/*
 * This handles formatting the new product then saving it to the database
 */
export const saveUpdateProduct = (
    item: ProductLineItem,
    field: string,
    subfield: string | null | undefined,
    value: string,
    handleSave: (index: number, updatedProduct: ProductLineItem) => void,
    index: number
) => {
    const newProduct = {...item} as const;
    if (subfield) {
        // @ts-expect-error
        newProduct[field][subfield] = value;
    } else {
        // @ts-expect-error
        newProduct[field] = value;
    }
    handleSave(index, newProduct);
};

/*
 * This handles when a new product is added to the quote
 * When an existing product is selected, we need to auto fill in the other fields
 * When 'Other' is selected, we simply set the name to 'Other'
 */
export const onNewProductName = (
    id: string,
    item: ProductLineItem,
    index: number,
    quotePossibleProductsEdges: Array<QuotePossibleProductsEdge>,
    handleSave: (index: number, updatedProduct: ProductLineItem) => void
) => {
    const newProduct = {...item} as const;
    const possibleProduct =
        quotePossibleProductsEdges &&
        quotePossibleProductsEdges.find((product) => product.node.id === id);
    if (possibleProduct) {
        // @ts-expect-error
        newProduct.product = {
            __typename: 'Product',
            id: possibleProduct.node.id,
        };
        // @ts-expect-error
        newProduct.name = possibleProduct.node.name;
        // @ts-expect-error
        newProduct.sku = possibleProduct.node.sku;
        // @ts-expect-error
        newProduct.term = possibleProduct.node.term;
        // @ts-expect-error
        newProduct.description = possibleProduct.node.description || '';
        newProduct.price.amount = possibleProduct.marketPrice
            ? possibleProduct.marketPrice.amount
            : 0;
    } else {
        // @ts-expect-error
        newProduct.name = DEFAULT_PRODUCT_NAME;
    }
    handleSave(index, newProduct);
};

export const getProductLineItemDefaults = (
    numItems: number,
    newProductCounter: number,
    products: Array<ProductLineItem>,
    quoteTotal: Money
) => {
    return {
        __typename: 'ProductLineItem',
        id: `${TEMP_NEW_PRODUCT_ID}-${newProductCounter}`,
        name: '',
        description: '',
        product: null,
        price: {
            __typename: 'Money',
            amount: 1,
            currency: quoteTotal.currency,
            formatted: `${quoteTotal.symbol}1`,
            formattedWithCents: `${quoteTotal.symbol}1.00`,
            symbol: quoteTotal.symbol,
        },
        position: numItems + 1,
        sku: '',
        quantity: 1,
        formattedQuantity: '1',
        term: null,
        discountUnit: 'PERCENT',
        discountNumber: 0,
        formattedDiscount: '',
        total: {
            __typename: 'Money',
            amount: 1,
            currency: quoteTotal.currency,
            formatted: `${quoteTotal.symbol}1`,
            formattedWithCents: `${quoteTotal.symbol}1.00`,
            symbol: quoteTotal.symbol,
        },
    };
};

export const mapTermEnumToLabel = (term: string) => {
    switch (term) {
        case 'MINUTE':
            return 'Minute';
        case 'HOUR':
            return 'Hour';
        case 'DAY':
            return 'Day';
        case 'WEEK':
            return 'Week';
        case 'MONTH':
            return 'Month';
        case 'QUARTER':
            return 'Quarter';
        case 'YEAR':
            return 'Year';
    }
};

export const shouldDisableDiscountToggle = (
    isTemplate: boolean,
    products: Array<ProductLineItem>,
    setShowDiscount: (arg1: boolean) => void
) => {
    if (isTemplate) {
        return false;
    }

    const hasADiscount = products.some((product) => product.discountNumber);
    if (hasADiscount) setShowDiscount(true);

    return hasADiscount;
};

// Ensures we show _something_ for discount unit option when displayed.
export const getDiscountUnitOption = (
    discountUnitOptions: Array<any>,
    discountUnit?: string | null
) => {
    const selectedOption = discountUnitOptions.find((option) => option.value === discountUnit);

    return selectedOption || discountUnitOptions[0];
};

// Does our best to format the total of the product line item based
// on the following: price, quantity, and discount.
//
// This will override the current market on the user in favor of their
// locale, which is probably incorrect.
export const getFormattedTotal = (item: ProductLineItem) => {
    const quantity = item.quantity || 0;
    // @ts-expect-error
    const priceAmount = item.price && item.price.amount ? parseFloat(item.price.amount) : 0;
    const currencyShortcode = item.price.currency;
    const discountUnit = item.discountUnit || 'PERCENT';

    // Calculate base total: price * quantity
    let total = (isNaN(priceAmount) ? 0 : priceAmount) * (isNaN(quantity) ? 0 : quantity);

    // Apply discount if present
    if (item.discountNumber != null && discountUnit) {
        // @ts-expect-error
        const discountNumber = parseFloat(item.discountNumber);
        if (!isNaN(discountNumber)) {
            if (discountUnit === 'FIXED_AMOUNT') {
                total = total - discountNumber;
            } else if (discountUnit === 'PERCENT') {
                const discountAmount = total * (discountNumber / 100);
                total = total - discountAmount;
            }
        }
    }

    // Ensure total is a valid number
    const finalTotal = isNaN(total) ? 0 : total;

    // Format using the currency shortcode
    return formatCurrencyValue(finalTotal, currencyShortcode);
};
